import { ComponentProps, createContext, ReactNode, useContext, useMemo } from 'react'
import { cn } from '~utils/cn'

import Avatar from '~components/ui/avatar'
import Icon from '~components/ui/icon'

type Size = 'sm' | 'md' | 'lg' | 'xl'

type InlineProfileMainProps = Readonly<
  ComponentProps<'div'> & {
    size?: Size
  }
>

type InlineProfileAvatarProps = Readonly<{
  thumbnailUri: string
}>

type InlineProfileHeaderProps = Readonly<{
  nickname: string
  createdAt?: string
  isAuthenticated?: boolean
}>

type ContextType = {
  size: Size
}

const InlineProfileContext = createContext<ContextType | undefined>(undefined)

const useInlineProfileContext = () => {
  const context = useContext(InlineProfileContext)

  if (!context) {
    throw new Error('useInlineProfileContext must be used within a InlineProfileProvider')
  }

  return context
}

const InlineProfileMain = ({
  size = 'sm',
  children,
  className,
  ...props
}: InlineProfileMainProps) => {
  const context = useMemo(() => ({ size }), [size])

  return (
    <InlineProfileContext.Provider value={context}>
      <div {...props} className={cn('flex items-center flex-gap-x-2', className)}>
        {children}
      </div>
    </InlineProfileContext.Provider>
  )
}

const InlineProfileAvatar = ({ thumbnailUri }: InlineProfileAvatarProps) => {
  const { size } = useInlineProfileContext()

  const avatarSize = {
    sm: '24' as const,
    md: '24' as const,
    lg: '32' as const,
    xl: '48' as const,
  }[size]

  return <Avatar uri={thumbnailUri} size={avatarSize} />
}

const InlineProfileHeader = ({
  nickname,
  createdAt,
  isAuthenticated,
}: InlineProfileHeaderProps) => {
  const { size } = useInlineProfileContext()

  return (
    <div className="flex items-center">
      <div
        className={cn('text-gray-900 dark:text-white', {
          'text-label-3': size === 'sm',
          'text-body-3': size === 'md',
          'text-title-3': size === 'lg' || size === 'xl',
        })}
      >
        {nickname || 'Unknown'}
      </div>
      {isAuthenticated && <Icon name="badge" className="ml-[2px] h-5 w-5 fill-primary-500" />}
      {createdAt && (
        <div className="flex shrink-0 items-center text-gray-500">
          <div className="mx-1 text-body-2">{'\u2022'}</div>
          <time
            className={cn({
              'text-label-3': size === 'sm',
              'text-body-3': size === 'md' || size === 'lg',
            })}
          >
            {createdAt}
          </time>
        </div>
      )}
    </div>
  )
}

const ProfileContent = ({ children }: { children: ReactNode }) => (
  <div className="text-gray-900 text-body-2">{children}</div>
)

const InlineProfile = Object.assign(InlineProfileMain, {
  Avatar: InlineProfileAvatar,
  Header: InlineProfileHeader,
  Content: ProfileContent,
})

export default InlineProfile
